import { User } from './../../../../shared/models/user.model';
import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { ApplicationUserService } from 'src/app/core/applicationuser.service';

@Component({
  selector: 'app-application-users-detail',
  templateUrl: './application-users-detail.component.html',
  styleUrls: ['./application-users-detail.component.scss'],
})
export class ApplicationUsersDetailComponent implements OnInit {
  userDetails: FormGroup;
  passwordMinLength = 6;
  editMode: boolean = false;
  uId = '';
  isUserNameValid: boolean = false;
  tempUserForm: any;
  get username() {
    return this.userDetails.get('username');
  }
  get password() {
    return this.userDetails.get('password');
  }
  get contactEmail() {
    return this.userDetails.get('contactEmail');
  }

  get sinequaProfile() {
    return this.userDetails.get('sinequaProfile');
  }

  get company() {
    return this.userDetails.get('company');
  }

  get admin() {
    return this.userDetails.get('admin');
  }

  get user() {
    return this.userDetails.get('user');
  }

  navigateToList() {
    if (this.editMode) {
      if (this.isFormValuesChanged()) {
        this.displayConfirmationDialogFromBack = true;
      } else {
        this.router.navigate(['/application-user-list']);
      }
    } else {
      if (!this.userDetails.pristine) {
        this.displayConfirmationDialogFromBack = true;
      } else {
        this.router.navigate(['/application-user-list']);
      }
    }
  }

  goBack() {
    this.router.navigate(['/application-user-list']);
  }
  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    public auService: ApplicationUserService,
    private notifierService: NotifierService,
    private route: ActivatedRoute // Use activatedRoute instead of route
  ) {
    this.userDetails = this.formBuilder.group({
      username: [
        '',
        [Validators.required, this.usernameValidator.bind(this)], // Bind the validator function to the component instance
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(this.passwordMinLength),
          Validators.pattern(
            /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])(?:(?!(?:[^@]*@[^@]+))[\s\S])*$/
          ),
        ],
      ],
      role: ['', Validators.required], // Add the 'role' form control
      sinequaProfile: ['', Validators.required], // Add the 'sinequaProfile' form control
      contactEmail: ['', [Validators.required, Validators.email]], // Add the 'contactEmail' form control
      company: ['', Validators.required], // Add the 'company' form control
      // Add other form controls as needed
    });
  }
  private populateForm(user: any) {
    // Use the received user data to populate your form
    this.userDetails.patchValue({
      username: user.username,
      password: user.password,
      role: user.role,
      sinequaProfile: user.sinequaProfile,
      contactEmail: user.contactEmail,
      company: user.company,
      // Populate other form controls as needed
    });
  }
  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.uId = params['uid'];
      const uid = params['uid'];
      const user = this.auService.getUser();
      this.tempUserForm = user;
      if (uid && user) {
        // Populate your form with the complete user object
        this.populateForm(user);
        this.editMode = true;
        this.username?.disable?.();
      }
    });
  }

  ngOnDestroy() {
    // Clear user details when leaving the detail page
    this.auService.clearUser();
  }

  displayConfirmationDialog = false;
  displayConfirmationDialogFromBack = false;
  confirmReset() {
    if (!this.userDetails.pristine) {
      this.displayConfirmationDialog = true;
    } else {
      // The form is pristine (empty), do something different
      this.notifierService.notify('warning', 'No changes available to cancel');
    }
  }

  // Function to reset the form values
  resetFormValues() {
    if (!this.editMode) {
      this.userDetails.reset(); // Reset the form
    } else {
      this.userDetails.patchValue(this.auService.getUser());
    }
    this.displayConfirmationDialog = false; // Close the dialog
  }

  // Function to cancel the reset
  cancelReset() {
    this.displayConfirmationDialog = false; // Close the dialog
    this.displayConfirmationDialogFromBack = false;
  }
  onSubmit({
    username,
    password,
    role,
    sinequaProfile,
    contactEmail,
    company,
    uid,
  }: {
    username: string;
    password: string;
    role: string;
    sinequaProfile: string;
    contactEmail: string;
    company: string;
    uid: string;
  }) {
    this.usernameValidator;
    if (this.editMode && !this.userDetails.dirty) {
      this.notifierService.notify('warning', 'No changes available to save.');
      return;
    }
    if (this.editMode) {
      if (!role || !sinequaProfile || !contactEmail || !company) {
        this.notifierService.notify(
          'error',
          'Please fill in all required fields before saving.'
        );
      } else {
        const username = this.userDetails?.get('username')?.value;
        const password = this.userDetails?.get('password')?.value;
        if (!this.validatePassword(password) && password != null) {
          // Display an error message for invalid password
          this.notifierService.notify(
            'error',
            'Invalid password. Please check the password requirements.'
          );
          return;
        }
        if (this.isUserNameValid) {
          this.auService.editUserDetails(
            username,
            role,
            sinequaProfile,
            contactEmail,
            company,
            this.uId,
            password
          );
        } else {
          this.notifierService.notify(
            'error',
            'Username contains special characters. Please enter a valid username.'
          );
        }
      }
    } else {
      // Check if any of the required form fields are null or empty
      if (
        !username ||
        !password ||
        !role ||
        !sinequaProfile ||
        !contactEmail ||
        !company
      ) {
        // Display an error message or handle the empty fields as needed
        this.notifierService.notify(
          'error',
          'Please fill in all required fields before saving.'
        );
      } else {
        if (!this.validatePassword(password)) {
          // Display an error message for invalid password
          this.notifierService.notify(
            'error',
            'Invalid password. Please check the password requirements.'
          );
          return;
        }
        if (this.isUserNameValid) {
          this.auService.saveCredentialsToFireBase(
            username,
            password,
            role,
            sinequaProfile,
            contactEmail,
            company
          );
        } else {
          this.notifierService.notify(
            'error',
            'Username contains special characters. Please enter a valid username.'
          );
        }
      }
    }
  }
  // Function to validate the password
  private validatePassword(password: string): boolean {
    return (
      !!password && // Using double negation to ensure a boolean value
      password.length >= this.passwordMinLength &&
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])(?:(?!(?:[^@]*@[^@]+))[\s\S])*$/.test(
        password
      )
    );
  }
  usernameValidator(control: AbstractControl) {
    const username = control.value;
    this.isUserNameValid =
      !username || !/[!@#$%^&*()_+|~=`{}\[\]:";'<>?,.\/]/.test(username);
    return this.isUserNameValid ? null : { containsSpecialCharacter: true };
  }
  isFormValuesChanged(): boolean {
    const formValues = this.userDetails.value;
    const userServiceValues = this.tempUserForm;

    // Define keys to compare
    const keysToCompare = [
      'role',
      'sinequaProfile',
      'contactEmail',
      'company',
      'password',
    ];

    // Compare values of specified keys
    for (const key of keysToCompare) {
      if (formValues[key] !== userServiceValues[key]) {
        return true; // Values are different
      }
    }

    return false; // All specified key values are the same
  }
}
